import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { setSentryUser } from '@/lib/sentry'
import { initPushNotifications, removeNativePushListeners } from '../lib/push-notifications'

interface AuthContextType {
  user: User | null
  session: Session | null
  isLoading: boolean
  isSignedIn: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, name?: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const pushInitialized = useRef(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
      setIsLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
      setIsLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Initialize push notifications once user is authenticated
  useEffect(() => {
    if (user && !pushInitialized.current) {
      pushInitialized.current = true
      initPushNotifications(async () => {
        const { data } = await supabase.auth.getSession()
        return data.session?.access_token ?? null
      }).catch((err) => {
        console.error('Push notification init failed:', err)
        pushInitialized.current = false
      })
    }

    // Cleanup listeners on sign-out
    if (!user && pushInitialized.current) {
      pushInitialized.current = false
      removeNativePushListeners().catch(() => {})
    }
  }, [user])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signUp = async (email: string, password: string, name?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } }
    })
    if (error) throw error
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` }
    })
    if (error) throw error
  }

  const getToken = async () => {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }

  return (
    <AuthContext.Provider value={{ user, session, isLoading, isSignedIn: !!user, signIn, signUp, signOut, signInWithGoogle, getToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
